import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import AnimationPlayer from 'components/AnimationPlayer/AnimationPlayer';
import { playMultipleSounds } from 'utils/play-multiple-sounds';
import InactivityManager from 'utils/InactivityManager';
import PlyrWithClosedCaptions from '@lwtears/lwt-common-frontend/lib/PlayerWithClosedCaptions';
import { captionFormat } from 'utils/caption-util';
import { formatPlyrVolume } from 'utils/plyr-util';
import { getLocale } from '@lwtears/lwt-common-frontend/lib/@common/util/i18n-util';
import { withLwtStore } from '@lwtears/lwt-common-frontend/lib/LwtApp';
import styles from './WallCard.module.scss';

const REASON = {
  didNotTouchCard: 'Did not touch the card'
};

const TIMEOUT = process.env.REACT_APP_INACTIVITY_TIMEOUT;

const CONTAINER_STYLE = {
  width: 'auto',
  height: '100%',
  maxWidth: '400px'
};

const PLYR_STYLE_OVERRIDE = {
  minWidth: '400px',
  minHeight: '400px',
  height: '100%',
  width: 'auto'
};

class WallCard extends React.Component {
  constructor(props) {
    super(props);
    this.state = { canPlay: false, roundEnd: false };
    this.vidRef = React.createRef();
    this.promise = null;
    this.inactivityManager = null;
    this.hasAnimation = !!props.currentRound.data.animation;
  }

  componentDidMount() {
    const {
      audio,
      data: {
        instructions: { beforeAnimation, introduction }
      }
    } = this.props.currentRound;

    this.promise = playMultipleSounds(500, audio, introduction)
      .then(() => playMultipleSounds(500, audio, beforeAnimation))
      .then(() => this.setState({ canPlay: true }))
      .then(() => this.startInactivityManger(beforeAnimation));
  }

  startInactivityManger = ({ src }) => {
    const { audio } = this.props.currentRound;

    const reminder = audio[src];

    this.inactivityManager = new InactivityManager(
      TIMEOUT,
      () => reminder.play(),
      REASON.didNotTouchCard
    );
    this.inactivityManager.watch();
  };

  playVideo() {
    const {
      state: { canPlay }
    } = this;
    if (!canPlay) return;
    this.inactivityManager && this.inactivityManager.clear();
    this.inactivityManager && this.inactivityManager.stopAudio();
    if (this.playedVideo) return;
    this.promise && this.promise.cancel();
    this.props.currentRound.stopAllSounds();
    this.playedVideo = true;
    this.vidRef.current?.play();
  }

  handleOnEnd = () => {
    this.setState({ ...this.state, roundEnd: true });

    this.playVideo();
  };
  componentWillUnmount() {
    this.promise && this.promise.cancel();
    this.props.currentRound.stopAllSounds();
    this.inactivityManager && this.inactivityManager.clear();
    this.inactivityManager && this.inactivityManager.stopAudio();
  }

  render() {
    const {
      props: {
        currentRound: {
          data: { animation, image }
        }
      },
      state: { canPlay }
    } = this;

    const {
      accessibility: {
        closedCaptionsSettings,
        audioSettings,
        videoNarrationSettings: { showSettings }
      }
    } = this.props.lwtStoreState.settings;

    const PlyrProps = {
      src: animation.src,
      type: 'video/mp4',
      aspectRatio: '1.5',
      title: '',
      onDone: this.props.onRoundEnd,
      options: {
        autoplay: true,
        withNativeControls: true,
        locale: getLocale(),
        narrationsAsDefault: showSettings,
        showSettings: showSettings,
        captions: {
          active: closedCaptionsSettings.showClosedCaptions,
          styles: { fontSize: closedCaptionsSettings.closedCaptionSize },
          hideCaptionToggle: true
        },
        plyrStyle: PLYR_STYLE_OVERRIDE,
        disableResize: true,
        containerStyles: CONTAINER_STYLE
      },
      config: { volume: formatPlyrVolume(audioSettings.volume, audioSettings.muteSounds) },
      captions: [captionFormat(animation.vtt)],
      narration: {
        src: animation.narrationSrc ?? '',
        captions: [{ src: animation.narrationVtt ?? '', label: '', kind: 'captions' }]
      }
    };

    return (
      <div className={styles.wallCard}>
        <div
          role="button"
          tabIndex="0"
          id="wall-card-image"
          className={!this.state.roundEnd ? styles.wallCardImage : ''}
          data-active={canPlay}
          onClick={this.handleOnEnd}
          onKeyDown={ev => ev.key === 'Enter' && this.handleOnEnd()}
          aria-label={image.description}
        >
          {!this.state.roundEnd ? (
            <AnimationPlayer
              canReplay={false}
              ended={this.props.onRoundEnd}
              passedRef={this.vidRef}
              poster={image.src}
              source={animation.src}
              width={400}
              autoplay={false}
            />
          ) : (
            <PlyrWithClosedCaptions {...PlyrProps} />
          )}
        </div>
      </div>
    );
  }
}

WallCard.propTypes = {
  currentRound: PropTypes.object,
  onRoundEnd: PropTypes.func,
  lwtStoreState: PropTypes.object
};

const mapStateToProps = () => ({});

export default connect(mapStateToProps)(withLwtStore(WallCard));
